/**
 * Shared log line renderer for displaying logs with ANSI colors, timestamps, and labels
 * Includes search highlighting and W&B run ID linking by default
 */

import {Alias} from '@wandb/weave/components/Tag';
import {AnsiUp} from 'ansi_up';
import React from 'react';

import {isValidDate} from '../../../util/date';
import {escapeHTML} from '../../util/html';
import {DateFromUTCString} from '../../util/time';
import {
  MAX_VISIBLE_LINE_LENGTH,
  REGEX_RUN_ID,
  TRUNCATED_LABEL_MAX_CHARS,
} from './constants';
import {LogLabel} from './LogLabel';
import {formatLogTimestamp, type LogTimestampFormatters} from './logTimestamp';

const EMPTY_SEARCH_MATCHES: SearchMatches = {indexed: {}, ordered: []};

export interface LogLineData {
  id: string;
  line: string;
  level?: string | null;
  label?: string | null;
  timestamp: string;
  lineNum?: number;
}

/**
 * Search match information for highlighting
 */
export type SearchMatch = {
  row: number;
  col: number;
};

export interface SearchMatches {
  indexed: {[row: number]: number[]};
  ordered: SearchMatch[];
}

export interface LogLineRendererProps {
  line: LogLineData;
  /** Row index for search highlighting */
  index: number;
  showTimestamp?: boolean;
  /** Formatters for rendering the timestamp in the host time zone */
  formatters: LogTimestampFormatters;
  showLabel?: boolean;
  showLineNumber?: boolean;
  maxLineNumLength?: number;
  /** Maximum label length for alignment (0 means no labels) */
  maxLabelLength?: number;
  style?: React.CSSProperties;
  className?: string;
  /** Search text for highlighting */
  searchText?: string;
  /** Search match locations */
  searchMatches?: SearchMatches;
  /** Currently active search match index */
  searchActive?: number;
}

// ANSI escape codes for search highlighting
const ANSI_BACKGROUND_YELLOW = '\u001b[48;5;11m';
const ANSI_BACKGROUND_ORANGE = '\u001b[48;5;220m';
const ANSI_RESET = '\u001b[0m';

/**
 * Insert wrapper text at each location
 */
const wrapRanges = (
  text: string,
  indices: number[],
  length: number,
  start: (index: number) => string,
  end: (index: number) => string
) => {
  let result = '';
  let lastIndex = 0;
  for (const index of indices) {
    result += text.substring(lastIndex, index);
    const startText = start(index);
    const endText = end(index);
    result += startText + text.substring(index, index + length) + endText;
    lastIndex = index + length;
  }
  result += text.substring(lastIndex);
  return result;
};

/**
 * Convert raw log text to HTML with search highlighting and run ID linking
 */
const convertToHtml = (
  index: number,
  text: string,
  searchText: string,
  searchMatches: SearchMatches,
  searchActive: number
): string => {
  // Create new instance of ansi_up for each line to avoid unterminated escape codes
  const ansiUp = new AnsiUp();
  const lineMatches = searchMatches.indexed[index] ?? [];
  let activeIndex = -1;

  if (searchActive !== -1) {
    const active = searchMatches.ordered[searchActive];
    if (active.row === index) {
      activeIndex = active.col;
    }
  }

  // Add search highlighting using ANSI codes
  const highlighted = wrapRanges(
    text,
    lineMatches,
    searchText.length,
    idx =>
      idx === activeIndex ? ANSI_BACKGROUND_ORANGE : ANSI_BACKGROUND_YELLOW,
    () => ANSI_RESET
  );

  // Check for W&B run IDs and create links
  const match = REGEX_RUN_ID.exec(highlighted);
  if (match == null) {
    return ansiUp.ansi_to_html(highlighted.replace(/ /g, '\u00a0'));
  }

  const [, projectName, entityName, name] = match;
  const encodedName = encodeURIComponent(name);
  const url = new URL(
    `/${encodeURIComponent(entityName)}/${encodeURIComponent(projectName)}/runs/${encodedName}`,
    window.location.origin
  );
  const link = `<a href="${url.pathname}">${escapeHTML(encodedName)}</a>`;
  const parts = highlighted
    .split(name)
    .map(part => ansiUp.ansi_to_html(part.replace(/ /g, '\u00a0')));
  return parts.join(link);
};

/**
 * Renders a single log line with optional timestamp, label, and line number
 */
export const LogLineRenderer: React.FC<LogLineRendererProps> = ({
  line,
  index,
  showTimestamp = true,
  formatters,
  showLabel = true,
  showLineNumber = false,
  maxLineNumLength = 0,
  maxLabelLength = 0,
  style,
  className = '',
  searchText = '',
  searchMatches = EMPTY_SEARCH_MATCHES,
  searchActive = -1,
}) => {
  const timestamp = DateFromUTCString(line.timestamp);

  // Truncate extremely long lines for performance
  const rawText =
    line.line.length > MAX_VISIBLE_LINE_LENGTH
      ? line.line.substring(0, MAX_VISIBLE_LINE_LENGTH)
      : line.line;

  return (
    <div role="row" style={style} className={className}>
      <div className="flex gap-8">
        {showLineNumber && line.lineNum != null && (
          <span
            aria-label="line number"
            className="select-none text-wb-text-tertiary">
            {`${line.lineNum}`.padStart(maxLineNumLength, '\u00a0')}
          </span>
        )}
        <div className="flex flex-wrap gap-8 md:flex-nowrap">
          {showTimestamp && isValidDate(timestamp) && (
            <span data-testid="timestamp-alias" className="shrink-0">
              <Alias
                color="moon"
                label={formatLogTimestamp(timestamp, formatters)}
              />
            </span>
          )}
          {showLabel && maxLabelLength > 0 && (
            <LogLabel
              label={line.label}
              maxLabelLength={maxLabelLength}
              truncatedMaxChars={TRUNCATED_LABEL_MAX_CHARS}
            />
          )}
          {renderLineContent(
            index,
            rawText,
            searchText,
            searchMatches,
            searchActive
          )}
        </div>
      </div>
    </div>
  );
};

const ANSI_ESCAPE_CHAR = String.fromCharCode(0x1b);
const NBSP = String.fromCharCode(0xa0);

/**
 * renderLineContent returns the styled '<span>' for one log line, picking the
 * React-element fast path for ANSI-free, non-runId text and falling back to
 * the ansiUp + innerHTML path otherwise.
 */
function renderLineContent(
  index: number,
  rawText: string,
  searchText: string,
  searchMatches: SearchMatches,
  searchActive: number
): React.ReactNode {
  if (rawText.includes(ANSI_ESCAPE_CHAR) || REGEX_RUN_ID.test(rawText)) {
    const html =
      convertToHtml(index, rawText, searchText, searchMatches, searchActive) +
      '<br />';
    return (
      <span
        className="break-all"
        dangerouslySetInnerHTML={{
          // The ansi to HTML library takes care of escaping.
          // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
          __html: html,
        }}
      />
    );
  }

  const lineMatches = searchMatches.indexed[index] ?? [];
  const active =
    searchActive !== -1 ? searchMatches.ordered[searchActive] : undefined;
  const activeCol = active?.row === index ? active.col : -1;
  return (
    <span className="break-all">
      {renderHighlightedSegments(
        rawText,
        searchText.length,
        lineMatches,
        activeCol
      )}
      <br />
    </span>
  );
}

/**
 * renderHighlightedSegments returns the highlighted line as React children.
 * Caller must guarantee 'text' has no ANSI escapes and no run-id pattern; the
 * legacy ansiUp + innerHTML path covers those cases.
 */
function renderHighlightedSegments(
  text: string,
  matchLength: number,
  matches: readonly number[],
  activeCol: number
): React.ReactNode {
  const visible = (s: string) => s.replace(/ /g, NBSP);

  if (matches.length === 0 || matchLength === 0) {
    return visible(text);
  }

  const nodes: React.ReactNode[] = [];

  let cursor = 0;
  for (let i = 0; i < matches.length; i++) {
    const col = matches[i];
    if (col > cursor) {
      nodes.push(visible(text.substring(cursor, col)));
    }

    nodes.push(
      <span
        key={i}
        // NOTE: Match these colors to ansiUp's rendering of ANSI_BACKGROUND_*
        // codes so the fast and fallback paths render identically.
        className={col === activeCol ? 'bg-[#ffd700]' : 'bg-[#ffff55]'}>
        {visible(text.substring(col, col + matchLength))}
      </span>
    );

    cursor = col + matchLength;
  }

  if (cursor < text.length) {
    nodes.push(visible(text.substring(cursor)));
  }

  return nodes;
}
